/**
 * Display formatting only. The UI never computes money; it formats what the API sends.
 *  - money: Intl currency with the ISO code shown, so `USD 1,234.56` and `CAD 1,234.56` are never ambiguous
 *  - dates: `15 Oct 2026` / `15 oct 2026`, spelled month so day and month are never swapped
 */

import type { Lang, Money } from '@portal-contract';

const MONEY_LOCALE: Record<Lang, string> = { en: 'en-US', es: 'es-419' };
const MONTHS: Record<Lang, readonly string[]> = {
  en: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
  es: ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'],
};

const formatters = new Map<string, Intl.NumberFormat | null>();

function numberFormat(lang: Lang, currency: string): Intl.NumberFormat | null {
  const key = `${lang}:${currency}`;
  if (!formatters.has(key)) {
    try {
      formatters.set(key, new Intl.NumberFormat(MONEY_LOCALE[lang], { style: 'currency', currency, currencyDisplay: 'code' }));
    } catch {
      formatters.set(key, null);
    }
  }
  return formatters.get(key) ?? null;
}

/** `USD 1,234.56`. Non-breaking space between code and number so a wrapped line never separates them. */
export function formatAmount(amount: number, currency: string, lang: Lang): string {
  const nf = numberFormat(lang, currency);
  const text = nf ? nf.format(amount) : `${currency} ${amount.toFixed(2)}`;
  return text.replace(/\s/g, ' ');
}

export const formatMoney = (m: Money, lang: Lang): string => formatAmount(m.amount, m.currency, lang);

/** Plain number with two decimals and grouping, for table cells under a column that already names the currency. */
export function formatNumber(amount: number, lang: Lang): string {
  return new Intl.NumberFormat(MONEY_LOCALE[lang], { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount);
}

/** Most receivables are USD, so USD leads; the rest follow alphabetically. */
export function compareCurrencies(a: string, b: string): number {
  if (a === b) return 0;
  if (a === 'USD') return -1;
  if (b === 'USD') return 1;
  return a.localeCompare(b);
}

const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** `2026-10-15` to `15 Oct 2026`. Anything that is not YYYY-MM-DD is returned unchanged. */
export function formatDate(iso: string, lang: Lang): string {
  const m = DATE_RE.exec(iso);
  if (!m) return iso;
  const month = MONTHS[lang][Number(m[2]) - 1];
  if (!month) return iso;
  return `${Number(m[3])} ${month} ${m[1]}`;
}

/** ISO timestamp to `15 Oct 2026, 11:00` in the viewer's time zone (or `timeZone`, for tests). */
export function formatDateTime(iso: string, lang: Lang, timeZone?: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const parts = new Intl.DateTimeFormat('en-GB', {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
    ...(timeZone ? { timeZone } : {}),
  }).formatToParts(d);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '';
  return `${formatDate(`${get('year')}-${get('month').padStart(2, '0')}-${get('day').padStart(2, '0')}`, lang)}, ${get('hour')}:${get('minute')}`;
}

// ---- calendar math on YYYY-MM-DD strings (no time zones involved) ----

export function parseDay(iso: string): { y: number; m: number; d: number } | null {
  const m = DATE_RE.exec(iso);
  return m ? { y: Number(m[1]), m: Number(m[2]), d: Number(m[3]) } : null;
}
const pad = (n: number): string => String(n).padStart(2, '0');
export const toDay = (y: number, m: number, d: number): string => `${y}-${pad(m)}-${pad(d)}`;
export function lastDayOfMonth(y: number, m: number): number {
  return new Date(Date.UTC(y, m, 0)).getUTCDate();
}
/** First day of the month `delta` months from `iso`'s month. */
export function firstOfMonth(iso: string, delta = 0): string {
  const p = parseDay(iso)!;
  const idx = p.y * 12 + (p.m - 1) + delta;
  return toDay(Math.floor(idx / 12), (idx % 12) + 1, 1);
}
export function lastOfMonth(iso: string, delta = 0): string {
  const first = parseDay(firstOfMonth(iso, delta))!;
  return toDay(first.y, first.m, lastDayOfMonth(first.y, first.m));
}

/** `acme-coffee-co` style slug for download file names. */
export function slug(s: string): string {
  return (
    s
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 40) || 'account'
  );
}
